/**
 * IrrigationOverview — the irrigation & sensors landing page.
 *
 * Answers "what is happening on the farm right now?" before it offers anything
 * to click: a live map with the watering areas lit up, then each open valve
 * with how long it has run and how much longer it has, then what is due next,
 * then anything that will stop water arriving. Device management, programs and
 * setup all live one click deeper.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ChevronRight,
  Droplets,
  Map as MapIcon,
  Radio,
  RefreshCw,
  Settings,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import FarmOverviewCard from '../../components/irrigation/FarmOverviewCard';
import IrrigationLiveMap from '../../components/irrigation/IrrigationLiveMap';
import IrrigationStatusStrip from '../../components/irrigation/IrrigationStatusStrip';
import RunningZoneCard from '../../components/irrigation/RunningZoneCard';
import UpcomingRunsList from '../../components/irrigation/UpcomingRunsList';
import useIrrigationLive from '../../hooks/useIrrigationLive';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import '../../styles/GlobalPageStyles.css';
import '../../styles/IrrigationLive.css';

const UPCOMING_SHOWN = 5;

export default function IrrigationOverview() {
  // Device management is a licensed irrigation feature, not an RBAC one: the
  // /irrigation/espnow/admin route and the backend registry both require the
  // irrigation *admin* seat. Gating these links on the org-admin role instead
  // hid them from licensed users and showed them to unlicensed org admins.
  const { hasAccess: canManageDevices } = useModuleLicense('irrigation', 'admin');
  const { data, live, loading, refreshing, error, lastUpdated, refresh } = useIrrigationLive();
  const [focusFarmId, setFocusFarmId] = useState<number | null>(null);

  useAppRefresh(refresh);

  // Memoised: a fresh [] each render would re-run every downstream useMemo.
  const farms = useMemo(() => data?.farms ?? [], [data]);
  const multiFarm = farms.length > 1;

  // Everything below the map respects the farm filter.
  const runs = useMemo(
    () => (focusFarmId == null ? live.runs : live.runs.filter((r) => r.farmId === focusFarmId)),
    [live.runs, focusFarmId]
  );
  const upcoming = useMemo(
    () =>
      (focusFarmId == null
        ? live.upcoming
        : live.upcoming.filter((u) => u.farmId === focusFarmId)
      ).slice(0, UPCOMING_SHOWN),
    [live.upcoming, focusFarmId]
  );

  /**
   * Alerts follow the farm filter too: when someone has focused a farm, a
   * problem on another farm's controller is not what they are looking at.
   * Unassigned devices (farmId null) stay visible only in the all-farms view.
   */
  const alerts = useMemo(
    () =>
      focusFarmId == null
        ? live.alerts
        : live.alerts.filter((a) => a.farmId === focusFarmId),
    [live.alerts, focusFarmId]
  );

  const secondsSinceUpdate = lastUpdated
    ? Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000))
    : null;

  const hasUnassigned =
    data &&
    (data.unassigned.controllers.length > 0 ||
      data.unassigned.sensors.length > 0 ||
      data.unassigned.weather_stations.length > 0);

  const unassignedCount = data
    ? data.unassigned.controllers.length +
      data.unassigned.sensors.length +
      data.unassigned.weather_stations.length
    : 0;

  const nothingConfigured = data && farms.length === 0 && !hasUnassigned;

  return (
    <>
      <PageHeader
        title="Irrigation & Sensors"
        subtitle="Live farm status"
        icon={<Droplets className="h-6 w-6 text-blue-600" />}
        actionButtons={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refresh()}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 sm:mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Link to="/irrigation/network">
              <Button variant="outline" size="sm" aria-label="Network Map">
                <MapIcon className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Network</span>
              </Button>
            </Link>
            <Link to="/irrigation/espnow">
              <Button variant="outline" size="sm" aria-label="Devices">
                <Radio className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Devices</span>
              </Button>
            </Link>
            {canManageDevices && (
              <Link to="/irrigation/espnow/admin">
                <Button variant="outline" size="sm" aria-label="Add Device">
                  <Settings className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Add Device</span>
                </Button>
              </Link>
            )}
          </div>
        }
      />
      <div className="mx-auto max-w-7xl px-4 py-6">
        {loading ? (
          <div className="flex h-64 items-center justify-center text-gray-400">
            <RefreshCw className="mr-2 h-6 w-6 animate-spin" />
            Loading live irrigation status…
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
            <button
              type="button"
              className="ml-3 font-semibold underline"
              onClick={() => void refresh()}
            >
              Try again
            </button>
          </div>
        ) : nothingConfigured ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-gray-400">
            <Droplets className="h-10 w-10 opacity-30" />
            <p className="text-sm">No irrigation devices found.</p>
            <p className="text-xs">
              Add controllers, sensors or weather stations and assign them to a farm to get started.
            </p>
            {canManageDevices ? (
              <Link to="/irrigation/espnow/admin">
                <Button size="sm">Add a Device</Button>
              </Link>
            ) : (
              <p className="text-xs">
                Registering a device needs an Irrigation Admin license seat — ask your
                organisation admin to assign you one.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* ── Status strip: the headline answer ──────────────────── */}
            <IrrigationStatusStrip
              scopeFarms={focusFarmId == null ? farms : farms.filter((f) => f.id === focusFarmId)}
              runs={runs}
              upcoming={upcoming}
              routersOnline={live.routersOnline}
              routersTotal={live.routersTotal}
              hasSchedule={live.hasBridgeSchedule || live.hasScheduleData}
              secondsSinceUpdate={secondsSinceUpdate}
            >
              {/* Farm filter — only earns its space with more than one farm. */}
              {multiFarm && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFocusFarmId(null)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    focusFarmId == null
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  >
                    All farms
                  </button>
                  {farms.map((farm) => {
                    const active = live.runs.filter((r) => r.farmId === farm.id && r.confirmed).length;
                    return (
                      <button
                        key={farm.id}
                        type="button"
                        onClick={() => setFocusFarmId(farm.id)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                        focusFarmId === farm.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                      >
                        {farm.name}
                        {active > 0 && (
                          <span className="ml-1.5 rounded-full bg-blue-500 px-1.5 text-[10px] text-white">
                            {active}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </IrrigationStatusStrip>

            {/* ── Map + what's running ───────────────────────────────────────── */}
            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                {data && (
                  <IrrigationLiveMap data={data} runs={live.runs} focusFarmId={focusFarmId} />
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                    <Droplets className="h-4 w-4 text-blue-500" />
                    Running now
                  </h2>
                  {runs.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
                      No valves are open.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {runs.map((run) => (
                        <RunningZoneCard key={run.key} run={run} showFarm={focusFarmId == null} />
                      ))}
                    </div>
                  )}
                </div>

                <UpcomingRunsList upcoming={upcoming} showFarm={focusFarmId == null} />
              </div>
            </div>

            {/* ── Anything stopping water arriving ───────────────────────────── */}
            {alerts.length > 0 && (
              <div className="mt-6">
                <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Needs attention ({alerts.length})
                </h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {alerts.map((alert) => (
                    <Link
                      key={alert.key}
                      to={alert.href}
                      className={`flex items-start gap-3 rounded-xl border p-3 transition hover:shadow-sm ${
                      alert.severity === 'error'
                        ? 'border-red-200 bg-red-50 hover:border-red-300'
                        : 'border-amber-200 bg-amber-50 hover:border-amber-300'
                    }`}
                    >
                      <AlertTriangle
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                        alert.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                      }`}
                      />
                      <div className="min-w-0">
                        <p
                          className={`text-sm font-semibold ${
                          alert.severity === 'error' ? 'text-red-800' : 'text-amber-800'
                        }`}
                        >
                          {alert.title}
                        </p>
                        <p className="text-xs text-gray-600">{alert.detail}</p>
                      </div>
                      <ChevronRight className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* ── Farms ──────────────────────────────────────────────────────── */}
            {farms.length > 0 && (
              <div className="mt-8">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Farms
                </h2>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {(focusFarmId == null ? farms : farms.filter((f) => f.id === focusFarmId)).map(
                    (farm) => (
                      <FarmOverviewCard key={farm.id} farm={farm} onChanged={() => void refresh()} />
                    )
                  )}
                </div>
              </div>
            )}

            {/* ── Unassigned devices ─────────────────────────────────────────── */}
            {hasUnassigned && data && (
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">
                    Unassigned Devices ({unassignedCount})
                  </h3>
                  <p className="mb-3 text-xs text-gray-500">
                    These devices are not assigned to a farm. Assign them to see them on the map.
                  </p>
                  <div className="space-y-1 text-sm text-gray-600">
                    {data.unassigned.controllers.map((c) => (
                      <div key={c.controller_id} className="flex items-center gap-2">
                        <Radio className="h-4 w-4 text-gray-400" />
                        <span>{c.controller_name || c.controller_id}</span>
                        <span className="text-xs text-gray-400">
                          ({c.routers.length} node{c.routers.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                    ))}
                    {data.unassigned.sensors.map((s) => (
                      <div key={s.device_id} className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-gray-400" />
                        <span>{s.name || s.device_id}</span>
                        <span className="text-xs text-gray-400">({s.type_display_name})</span>
                      </div>
                    ))}
                    {data.unassigned.weather_stations.map((w) => (
                      <div key={w.id} className="flex items-center gap-2">
                        <Droplets className="h-4 w-4 text-gray-400" />
                        <span>{w.name}</span>
                        <span className="text-xs text-gray-400">(weather station)</span>
                      </div>
                    ))}
                  </div>
                  {canManageDevices && (
                    <div className="mt-3">
                      <Link to="/irrigation/espnow/admin">
                        <Button variant="outline" size="sm">
                          <Settings className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Manage Devices</span>
                          <span className="sm:hidden">Manage</span>
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}
